import {AxiosError} from 'axios';
import {
  BackendErrorData,
  parseApiError,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  AddProjectPayload,
  DomainDataResponse,
  EntityItem,
  GetHcodeParams,
  IAddProjectFieldGroupRequest,
  IAddProjectFieldRequest,
  IprintDownloadPayload,
  IProjectFieldAllDropdownListsResponse,
  IProjectFieldDetailsResponse,
  IUpdateDomainPayload,
  IUpdateProjectPayload,
} from '../types/project.type';
import BASE_URL from '../../config/env';
import {NotificationResponse} from '../types/notifications.type';
import {DestinationPath} from '../types/categorization.type';

export const projectManagementEndpoint = (path: string) =>
  `${BASE_URL}/projectmanagement/api/v1/project${path}`;

export const projectManagementNoProjectEndpoint = (path: string) =>
  `${BASE_URL}/projectmanagement/api/v1/${path}`;

export const getManageUsers = async (projectId: number) => {
  const {data} = await apiClient.get(
    projectManagementEndpoint(`/manage-users/${projectId}`)
  );
  return data;
};

export const getDomainUsers = async (entityId: number) => {
  const {data} = await apiClient.get(
    projectManagementEndpoint(`/mapping/domain-users/${entityId}`)
  );
  return data;
};

export const getProjectUsers = async (projectId: number) => {
  const {data} = await apiClient.get(
    projectManagementEndpoint(`/mapping/users/${projectId}`)
  );
  return data;
};

export const updateDomainUsers = async (payload: IUpdateDomainPayload) => {
  const {data} = await apiClient.post(
    projectManagementEndpoint('/mapping/domain'),
    payload
  );
  return data;
};

export const updateProjectUsers = async (payload: IUpdateProjectPayload) => {
  const {data} = await apiClient.post(
    projectManagementEndpoint('/mapping/project'),
    payload
  );
  return data;
};

export const getNotifications = async (): Promise<NotificationResponse[]> => {
  const {data} = await apiClient.get(
    projectManagementNoProjectEndpoint('notification')
  );
  return data;
};

export const postNotificationCount = async () => {
  const {data} = await apiClient.post(
    projectManagementNoProjectEndpoint('notification/notify/bit')
  );
  return data;
};

export const getNotificationCount = async (): Promise<number> => {
  const {data} = await apiClient.get(
    projectManagementNoProjectEndpoint('notification/notify/bit')
  );
  return data;
};

export const getMaxLimit = async (destination: string) => {
  const {data} = await apiClient.get(
    projectManagementNoProjectEndpoint(
      `projectconfiguration/config/doclist-docview-limit/${destination}`
    )
  );
  return data;
};

export const getLongSortLimit = async () => {
  const {data} = await apiClient.get(
    projectManagementNoProjectEndpoint(
      `projectconfiguration/config/long-sort-limit`
    )
  );
  return data;
};

export const getConfigKeyValue = async (
  payload: DestinationPath
): Promise<any> => {
  const {data} = await apiClient.get(
    projectManagementNoProjectEndpoint(`projectconfiguration/config/${payload}`)
  );
  return data;
};

export const getPrintReport = async (
  payload: IprintDownloadPayload
): Promise<Blob> => {
  const {data} = await apiClient.post(
    projectManagementNoProjectEndpoint('notification/download'),
    payload,
    {
      responseType: 'blob',
    }
  );
  return data;
};

export interface ProjectItem {
  key: string;
  value: string;
}

export interface ProjectListResponse {
  folder: ProjectItem[];
}

export const getProjectList = async (
  clientTypeId: number,
  clientId: number
): Promise<ProjectListResponse> => {
  const {data} = await apiClient.get<ProjectListResponse>(
    projectManagementEndpoint('/projects/list'),
    {
      params: {
        ClientTypeId: clientTypeId,
        ClientId: clientId,
      },
    }
  );
  return data;
};

export const getEntityList = async (
  domainType: number
): Promise<EntityItem[]> => {
  const {data} = await apiClient.get<EntityItem[]>(
    projectManagementNoProjectEndpoint('entity/entities'),
    {
      params: {domainType},
      headers: {
        Accept: 'text/plain',
      },
    }
  );

  return data;
};

export const getDomainData = async (
  entityId: number
): Promise<DomainDataResponse> => {
  const {data} = await apiClient.get<DomainDataResponse>(
    projectManagementEndpoint('/projects/domain-data'),
    {
      params: {entityId},
      headers: {
        Accept: 'text/plain',
      },
    }
  );
  return data;
};

export const getProjects = async (queryParams: string) => {
  const {data} = await apiClient.get(
    projectManagementEndpoint(`/projects/${queryParams}`)
  );
  return data;
};

export const fetchByHcode = async (params: GetHcodeParams) => {
  const {hcode, entityTypeId, selectedEntityId, projectId} = params;

  const response = await apiClient.get(
    projectManagementEndpoint(`/hcode-check`),
    {
      params: {
        hcode,
        entityTypeId,
        selectedEntityId,
        projectId,
      },
      headers: {
        Accept: 'text/plain',
      },
    }
  );

  return response.data;
};

export const fetchProjectDomainData = async (
  entityId: number
): Promise<any> => {
  const response = await apiClient.get(
    projectManagementEndpoint(`/projects/domain-data`),
    {
      params: {entityId},
    }
  );
  return response.data;
};

export const addProject = async (payload: AddProjectPayload): Promise<any> => {
  const response = await apiClient.post(
    projectManagementEndpoint('/add'),
    payload
  );
  return response.data;
};

export const getProjectFieldList = async (
  payload: unknown
): Promise<ProjectFieldListResponse> => {
  try {
    const {data} = await apiClient.post<ProjectFieldListResponse>(
      projectManagementNoProjectEndpoint(`projectfields/list`),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'projectFieldsList'
    );
  }
};

export const updateProjectFieldState = async (
  projectField: ProjectField
): Promise<any> => {
  try {
    const isFieldUpdated = await apiClient.patch<boolean>(
      projectManagementNoProjectEndpoint(
        `projectfields/${projectField.projectFieldId}/state`
      ),
      projectField.isActive,
      {
        headers: {'Content-Type': 'application/json'}, // <-- ensure JSON
      }
    );
    return isFieldUpdated;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'projectFieldsList'
    );
  }
};

export interface GetProjectFieldListRequestParams {
  filters?: string;
  SortBy?: string;
  SortingOrder?: string;
  PageNumber?: number;
  PageSize?: number;
}
export interface ProjectField {
  projectFieldId: number;
  projectFieldName: string;
  dataType: string;
  isSet: boolean;
  isSearchable: boolean;
  isActive: boolean;
  hasCodingForm: boolean;
  isSystemField: boolean;
}

export type ProjectFieldListResponse = {
  totalRecords: number;
  totalDisplayRecords: number;
  projectFields: ProjectField[];
};

export interface IUserDetailsRequest {
  entityId: number;
  isActive: boolean;
}

export interface IUserDetailsResponse {
  userId: number;
  firstName: string;
  lastName: string;
  roleName: string;
  status: string;
  securityGroupName: string;
  projectLabel: string;
  entityLabel: string;
  isBillable: boolean;
}

export const getDomainDashboardUsers = async (
  payload: IUserDetailsRequest
): Promise<IUserDetailsResponse[]> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {data} = await apiClient.get<IUserDetailsResponse[]>(
    `/projectmanagement/api/v1/domaindashboard/users/details`,
    {
      params: {
        EntityId: payload.entityId,
        IsActive: payload.isActive,
      },
    }
  );
};

export const EditProjectDetails = async (projectId: string) => {
  const response = await apiClient.get(
    projectManagementEndpoint(`/${projectId}/edit`)
  );
  return response.data;
};

export const getCreateProjectMeta = async () => {
  const {data} = await apiClient.get(projectManagementEndpoint('/meta'));
  return data;
};

export const getProjectFieldAllDropdownLists =
  async (): Promise<IProjectFieldAllDropdownListsResponse> => {
    const response =
      await apiClient.get<IProjectFieldAllDropdownListsResponse>(
        projectManagementNoProjectEndpoint(`projectfields/add/view`)
      );
    return response.data;
  };

export const addProjectFieldGroup = async (
  payload: IAddProjectFieldGroupRequest
): Promise<boolean> => {
  try {
    const response = await apiClient.post<boolean>(
      projectManagementNoProjectEndpoint(`projectfields/groups`),
      payload
    );
    return response.data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'addProjectFieldGroup'
    );
  }
};

export const addProjectField = async (
  payload: IAddProjectFieldRequest
): Promise<boolean> => {
  try {
    const response = await apiClient.post<boolean>(
      projectManagementNoProjectEndpoint(`projectfields/add`),
      payload
    );
    return response.data;
  } catch (error) {
    throw parseApiError(
      error as AxiosError<BackendErrorData>,
      'AddProjectField'
    );
  }
};

export const getProjectFieldDetails = async (
  projectId: string
): Promise<IProjectFieldDetailsResponse> => {
  const response = await apiClient.get<IProjectFieldDetailsResponse>(
    projectManagementNoProjectEndpoint(`projectfields/${projectId}`)
  );
  return response.data;
};

export const getProjectTemplate = async (projectId: string) => {
  const {data} = await apiClient.get(
    projectManagementEndpoint(`/projects/templates/${projectId}`)
  );
  return data;
};
